import _ from "lodash";
import { isDefined, isStringEmpty, getUrlParameters, getRandomSmallKey } from "@Utils/Micro";
import { SimpleEventDispatcher } from "ste-simple-events";
import { RestAPIInfo, BodyRawType } from '@Macros/RestAPIEditor';
import { MacroLogicEngine, MacroLogicResults } from '@Macros/Macro';
import { WebNode } from '@API/WebNode';
import { ReduxPayloadAction, StateStatus } from '@API/WebNodeTypes';
import { screenWidth, yHas } from '@Utils/Utils';
import { getGlobal } from '@Utils/Globals';
import { DebugEx } from '@Utils/DebugEx';
import { BoardThings } from './BoardThings';
import { Localization } from '@Primitive/Localization/Localization';
import * as ModelViews from '../../ModelViews';
import jwt_decode from 'jwt-decode';
import { MacroUserValidationFormManager } from './BoardMacroUserValidationForm';
import { UserManager } from '@Utils/UserManager';
import { ServerPaths } from '@Utils/Paths';
import { BoardTheme, BoardTheming } from "./BoardTheming";
// ------------------------------------------------------------------------------------------------
export interface MacroDataInfo {
    /**
     * Type of src.
     * Note: now we support only port and register events type
     * Port: widget things port events
     * Register: virtual memory that can write/read from macros
     * Cyan: Receive data from cyan graphs
     */
    Type: "port" | "register" | "cyan";

    /**
     * The register topic for cyan
     */
    TopicName?: string;

    /**
     * Register name
     */
    RegisterName?: string;

    /**
     * Register is persistence between browser restart.
     */
    RegisterIsPersistence?: boolean;

    /**
     * In what page the widget is exist
     */
    PageId?: string;

    /**
     * The widget id that contain the port
     */
    ThingKey?: string;

    /**
     * The port id that contain the data
     */
    PortKey?: string;

    /**
     * The name of the variable in the result json
     */
    VariableName: string;

    /**
     * Set if this src is enabled
     */
    Enabled: boolean;

    /**
     * This can trigger the solve
     */
    IsTrigger: boolean;

    /**
     * Make UI widget in loading state until the macro finished 
     */
    UILoading: boolean;
}
// ------------------------------------------------------------------------------------------------
export const enum MacroType {
    /**
     * The macro is running in the initialization of the board and is not have any trigger points
     */
    Init = "init",

    /**
     * Macro that running from port/register events
     */
    Event = "event",

    /**
     * Less gem type
     */
    Less = "less"
}
// ------------------------------------------------------------------------------------------------
export interface MacroUserValidationFormInfo {
    /**
     * Enabled of the functionality
     */
    Enabled: boolean;

    /**
     * String of the dialog
     */
    Title: string;

    /**
     * Body text
     */
    Body: string;

    /**
     * Show Cancel button
     * Default: true
     */
    ShowCancelButton?: boolean;

    /**
     * Custom Cancel button label
     */
    CancelLabel?: string;

    /**
     * Custom Accept button label
     */
    AcceptLabel?: string;

    /**
     * Icon info
     */
    Icon?: string;
    IconColor?: string;
    IconSize?: number;

    /**
     * Define the width 
     */
    SizeWidth: number;

    /**
     * Define the width 
     */
    SizeHeight: number;
}
// ------------------------------------------------------------------------------------------------
export interface MacroSettings {

    /**
     * Define the type of macro
     */
    Type: MacroType;

    /**
     * Name of macro
     */
    Name: string;

    /**
     * Description of the macro
     */
    Description: string;

    /**
     * Path of the macro.
     * empty or / is indicate the root.
     */
    Path: string;

    /**
     * Settings for macro data src
     */
    MacroDataSrc: MacroDataInfo[];

    /**
     * Settings for macro data dst
     */
    MacroDataDst: MacroDataInfo[];

    /**
     * Order of the macro in the list
     */
    Order: number;

    /**
     * Information for user validation form
     */
    UserValidationForm?: MacroUserValidationFormInfo;

    /**
     * Run when we start the editor.
     * Valid for Type = 'init'
     */
    InitInEditor?: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface MacroInfoEx extends ModelViews.DashboardStructs.MacroInfo {
    /**
     * Dynamic format of the settings
     */
    Settings: MacroSettings;

    /**
     * Rest api settings
     */
    RestSettings: RestAPIInfo;
}
// ------------------------------------------------------------------------------------------------
export interface MacroJsPrepInput {
    Data: { [key: string]: any };
    MacroSrc: MacroDataInfo;
    Info: {
        UserEmail: string;
        URLParams: { [key: string]: string };
        URLHash: string;
        Language: string;
        Location: any;
        EndpointKey: string;
        inMobileMode: boolean;
        Hostname: string;
        WebSocketHostname: string;
        WebPushNotificationToken: string;
        BoardId: string;
        AccessToken: any;
        inDarkMode: boolean;
        BoardTheme: BoardTheme;
    }
}
// ------------------------------------------------------------------------------------------------
export function AggregateMacroDataSrc(
    nodeKey: string,
    data: MacroDataInfo[],
    dataTriggerIndex: number,
    register: { [key: string]: any },
    cyanRegister: { [key: string]: any }
): MacroJsPrepInput {
    let node = WebNode.GetInstanceKey(nodeKey);
    let results: MacroJsPrepInput = {
        Data: {},
        MacroSrc: null,
        Info: {
            URLParams: getUrlParameters(),
            URLHash: window.location.hash,
            Hostname: null,
            WebSocketHostname: null,
            Location: null,
            Language: null,
            UserEmail: null,
            EndpointKey: null,
            inMobileMode: false,
            WebPushNotificationToken: null,
            BoardId: null,
            AccessToken: null,
            inDarkMode: false,
            BoardTheme: BoardTheming.GetActiveTheme()
        }
    };

    let boardMacroEngine = BoardMacroEngine.GetInstance(nodeKey);
    if (isDefined(boardMacroEngine)) {
        results.Info.inMobileMode = screenWidth() <= boardMacroEngine.WidthMobileThreshold;
    }

    if (process.env.GLOW_BUILD) {
        results.Info.AccessToken = UserManager.GetInstance()?.getAccessToken();
    } else {
        results.Info.AccessToken = BoardMacroEngine.GetAccessToken();
    }

    if (isDefined(data) &&
        dataTriggerIndex < data.length)
        results.MacroSrc = data[dataTriggerIndex];

    if (isDefined(node)) {
        // Set the global vars that depend on the node
        results.Info.Location = node.Location;
        results.Info.UserEmail = node.YodiWS.UserInfo.Email;
        results.Info.EndpointKey = node.endpointKey;
        results.Info.Hostname = window.location.host;
        results.Info.WebSocketHostname = node.YodiWS.WebsocketUrl;
        results.Info.WebPushNotificationToken = getGlobal("web-push-notification-token");
        results.Info.BoardId = node.YodiWS.BoardId;

        let localization = Localization.GetInstance(nodeKey);
        if (isDefined(localization))
            results.Info.Language = localization.SelectedLanguage;

        _.forEach(data, d => {
            if (d.Enabled) {
                if (d.Type == "port") {
                    let w = node.GetThing(d.ThingKey);
                    if (isDefined(w)) {
                        let p = w.GetPort(d.PortKey);
                        if (isDefined(p)) {
                            if (p.LastStateStatus == StateStatus.Valid)
                                results.Data[d.VariableName] = p.State;
                            else
                                results.Data[d.VariableName] = {};
                        }
                    }
                } else if (d.Type == "register") {
                    results.Data[d.VariableName] = register[d.RegisterName];
                } else if (d.Type == "cyan") {
                    results.Data[d.VariableName] = cyanRegister[d.TopicName];
                }
            }
        });
    }

    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
        results.Info.inDarkMode = true;

    return results;
}
// ------------------------------------------------------------------------------------------------
export function getDefaultMacroSettings(type: MacroType): MacroSettings {
    return {
        Description: "",
        Name: "EmptyMacro-" + getRandomSmallKey(),
        MacroDataSrc: [],
        MacroDataDst: [],
        Path: "",
        Type: type,
        Order: 0,
        UserValidationForm: {
            Enabled: false,
            AcceptLabel: "OK",
            CancelLabel: "Cancel",
            Title: "",
            Body: "",
            Icon: "fad fa-exclamation-triangle",
            IconColor: "#AF7300",
            IconSize: 90,
            SizeHeight: 50,
            SizeWidth: 25
        }
    };
}
export function getDefaultRestAPIInfo(): RestAPIInfo {
    return {
        URLPrefix: "https://",
        BodyField: "",
        BodyFormData: [],
        BodyType: "raw",
        ContentType: BodyRawType.JSON,
        Headers: [],
        Parameters: [],
        Type: "GET",
        URL: "",
        IncludeCredentials: false,
        IncludeJWT: false
    };
}
// Transform json settings to actual settings
export function MacroSettingsUnpack(Macros: MacroInfoEx[] | { [key: string]: MacroInfoEx }, inEditMode: boolean = true) {
    _.forEach(_.filter(Macros as MacroInfoEx[], m =>
        (!isDefined(m.Settings)) ||
        (!isDefined(m.RestSettings) && m.MacroType == ModelViews.Macros.MacroScriptType.RestAPI)), m => {
            if (!isDefined(m.Settings)) {
                let def = getDefaultMacroSettings(MacroType.Event);

                try { m.Settings = _.merge(def, JSON.parse(m.SettingsJson)); }
                catch (ex) { m.Settings = def; }

                if (!inEditMode)
                    m.SettingsJson = null;
            }

            if (!isDefined(m.RestSettings) &&
                (m.MacroType == ModelViews.Macros.MacroScriptType.RestAPI)) {
                try { m.RestSettings = JSON.parse(m.QueryScript); }
                catch (ex) { m.RestSettings = getDefaultRestAPIInfo(); }

                if (!inEditMode)
                    m.QueryScript = null;
            }
        });
}
// ------------------------------------------------------------------------------------------------
export interface BoardMacroEngineLogInstance {
    Timestamp: string;
    JSPrepInput: any;
    JsPrepResults: any;
    QueryResults: any;
    QueryMetadata: any;
    JsHandlingResults: any;
    Error: any[];
    JsHandlingDst: any;
    Performance: {
        JSPrep?: number;
        JSPrepExec?: number;
        Query?: number;
        JSHandling?: number;
        JSHandlingExec?: number;
    }
    StoppedFromUser?: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface BoardMacroEngineLog {
    Macro: MacroInfoEx;
    Log: BoardMacroEngineLogInstance[];
    LastStateHaveErrors: boolean;
}
// ------------------------------------------------------------------------------------------------
interface UserTokenHandling {
    Timeout: Date;
    Token: string;
    Data: any;
}
interface IWidgetDataUpdate {
    ThingKey: string;
    PortKey: string;
    Data: any;
}
// ------------------------------------------------------------------------------------------------
export class BoardMacroEngine {
    // --------------------------------------------------------------------------------------------
    private static _tokenInstance: UserTokenHandling = null;
    private static _instances: { [key: string]: BoardMacroEngine } = {};
    public static GetInstance(nodeKey: string): BoardMacroEngine { return (_.has(this._instances, nodeKey)) ? this._instances[nodeKey] : null; }
    // --------------------------------------------------------------------------------------------
    private MacroLogicEngine: MacroLogicEngine;
    private BoardId: string;
    private NodeKey: string;
    private inEditMode: boolean;
    private Macros: MacroInfoEx[];
    // ThingKey=>PortKey=>Macro
    private MacroToThingMapping: { [key: string]: { [key: string]: MacroInfoEx[] } };
    private MacroToRegisterMapping: { [key: string]: MacroInfoEx[] };
    private onDataRegisterUpdate: SimpleEventDispatcher<string[]>;
    private onDataWidgetPortUpdate: SimpleEventDispatcher<IWidgetDataUpdate>;
    private onDataCyanUpdate: SimpleEventDispatcher<[string, any]>;
    // Macro ID => Log
    public MacroDebugHistory: { [key: string]: BoardMacroEngineLog };
    public Register: { [key: string]: any };
    public CyanRegister: { [key: string]: any };
    public WidthMobileThreshold: number;
    // --------------------------------------------------------------------------------------------
    constructor(nodeKey: string,
        boardId: string,
        Macros: MacroInfoEx[],
        inEditMode: boolean,
        widthMobileThreshold: number) {

        // Check if we already have init the nodekey
        if (_.has(BoardMacroEngine._instances, nodeKey)) {
            DebugEx.TraceWarning("Try to init board things again");
        }

        // Link the board things with this one
        BoardMacroEngine._instances[nodeKey] = this;
        this.inEditMode = inEditMode;
        this.BoardId = boardId;
        this.NodeKey = nodeKey;
        this.Register = {};
        this.CyanRegister = {};
        this.MacroToRegisterMapping = {};
        this.MacroDataLoadingCounting = {};
        this.MacroLogicEngine = new MacroLogicEngine();
        this.WidthMobileThreshold = widthMobileThreshold;
        this.setMacros(Macros);

        // Register to port updates
        let node = WebNode.GetInstanceKey(this.NodeKey);
        if (isDefined(node)) {
            node.onDataPortUpdate.subscribe(this.dataPortUpdated)
        }

        // Register register updates
        this.onDataRegisterUpdate = new SimpleEventDispatcher<string[]>();
        this.onDataRegisterUpdate.subscribe(this.dataRegisterUpdated);

        // Register port async updates
        this.onDataWidgetPortUpdate = new SimpleEventDispatcher<IWidgetDataUpdate>();
        this.onDataWidgetPortUpdate.subscribe(this.dataWidgetPortUpdated);

        // Register allocation
        this.onDataCyanUpdate = new SimpleEventDispatcher<[string, any]>();
        this.onDataCyanUpdate.subscribe(this.dataCyanUpdated);

        // Read the registers from local storage
        _.forEach(this.Macros, m => {
            _.forEach(m.Settings.MacroDataDst, md => {
                if (md.RegisterIsPersistence) {
                    this.Register[md.RegisterName] = JSON.parse(localStorage.getItem(this.getLocalStorageKey(md.RegisterName)));
                }
            });
        });
    }
    // --------------------------------------------------------------------------------------------
    public async initTokenSM() {
        if (!process.env.GLOW_BUILD &&
            BoardMacroEngine._tokenInstance == null)
            await BoardMacroEngine.StartTokenSM();
    }
    // --------------------------------------------------------------------------------------------
    private getLocalStorageKey(registerName: string) {
        return `gem-registers-${this.NodeKey}-${registerName}`;
    }
    // --------------------------------------------------------------------------------------------
    public setInEditMode(inEditMode: boolean) {
        this.inEditMode = inEditMode;
    }
    // --------------------------------------------------------------------------------------------
    public setMacros(macros: MacroInfoEx[]) {
        MacroSettingsUnpack(macros, this.inEditMode);
        this.Macros = macros;
        this.MacroDebugHistory = {};
        this.MacroToThingMapping = {};
        this.MacroToRegisterMapping = {};

        _.forEach(macros, m => {
            if (m.Enabled)
                _.forEach(m.Settings.MacroDataSrc, ms => {
                    if (ms.IsTrigger && ms.Enabled) {
                        if (ms.Type == "port") {
                            let tKey = ms.ThingKey;
                            let pKey = ms.PortKey;
                            if (!yHas(this.MacroToThingMapping, tKey)) {
                                this.MacroToThingMapping[tKey] = {};
                            }
                            if (!_.has(this.MacroToThingMapping[tKey], pKey)) {
                                this.MacroToThingMapping[tKey][pKey] = [];
                            }
                            this.MacroToThingMapping[tKey][pKey].push(m);
                        } else if (ms.Type == "register") {
                            let regName = ms.RegisterName;
                            if (!yHas(this.MacroToRegisterMapping, regName)) {
                                this.MacroToRegisterMapping[regName] = [];
                            }
                            this.MacroToRegisterMapping[regName].push(m);
                        }
                    }
                });
        });
    }
    // --------------------------------------------------------------------------------------------
    public getMacros(): readonly MacroInfoEx[] {
        return this.Macros;
    }
    // --------------------------------------------------------------------------------------------
    public static async runLessMacros(macros: MacroInfoEx[]) {
        let lessFiltering = _(macros).filter(x => x.Settings.Type == MacroType.Less && x.Enabled);
        if (lessFiltering.value().length == 0)
            return;

        let less = await import('less');

        // Render all less
        let lessOutputs = await Promise.all(
            lessFiltering
                .orderBy(x => x.Settings.Order, "asc")
                .map(async x => {
                    try {
                        return less.render(x.JsPrepScript);
                    } catch (ex) {
                        DebugEx.TraceError(ex);
                    }
                    return null;
                })
                .value()
        );

        // aggregate css
        let output = _(lessOutputs)
            .compact()
            .map(x => x.css)
            .join("\r\n");

        // Find style block
        let lastChild: HTMLStyleElement = document.getElementById("yodiwo-less-gem") as HTMLStyleElement;
        if (lastChild == null ||
            lastChild.type != "text/css" ||
            lastChild.id != "yodiwo-less-gem") {
            lastChild = document.head.appendChild(document.createElement("style"));
            lastChild.id = "yodiwo-less-gem";
            lastChild.type = "text/css";
        }

        lastChild.innerText = output;
    }
    // --------------------------------------------------------------------------------------------
    public async runLessMacros() {
        BoardMacroEngine.runLessMacros(this.Macros);
    }
    // --------------------------------------------------------------------------------------------
    public runInitMacros(inEditor: boolean = false) {
        return new Promise((resolve, reject) => {
            let node = WebNode.GetInstanceKey(this.NodeKey);
            if (!isDefined(node))
                return;

            let macrosQuery = _(this.Macros)
                .filter(m => m.Settings.Type == MacroType.Init);

            if (inEditor)
                macrosQuery = macrosQuery.filter(m => m.Settings.InitInEditor);

            let macros = macrosQuery
                .orderBy(m => m.Settings.Order, "asc")
                .value();

            if (macros.length > 0) {
                let count = macros.length;
                let successCB = (success) => {
                    count--;
                    if (count == 0)
                        resolve(true);
                };

                for (let m of macros) {
                    if (m.Enabled)
                        this.runMacro(m, 0, node, successCB);
                    else
                        successCB(true);
                }
            } else {
                resolve(true);
            }
        })
    }
    // --------------------------------------------------------------------------------------------
    private dataPortUpdated = (ports: ReduxPayloadAction[]) => {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        if (!isDefined(node))
            return;

        // Detect if we have match port
        _.forEach(ports, p => {
            let mThing = this.MacroToThingMapping[p.ThingKey];
            if (isDefined(mThing)) {
                let macros = mThing[p.PortKey];
                if (isDefined(macros)) {
                    _.forEach(macros, m => {
                        let srcIndex = _.findIndex(m.Settings.MacroDataSrc, mds =>
                            mds.Type == "port" &&
                            mds.PortKey == p.PortKey &&
                            mds.ThingKey == p.ThingKey
                        );
                        this.runMacro(m, srcIndex, node);
                    });
                }
            }
        });
    }
    // --------------------------------------------------------------------------------------------
    private dataCyanUpdated = (info: [string, any]) => {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        if (!isDefined(node))
            return;

        let topic = info[0];
        this.CyanRegister[topic] = info[1];
        _.forEach(this.Macros, m => {
            _.forEach(m.Settings.MacroDataSrc, (mds, srcIndex) => {
                if (m.Enabled &&
                    mds.Enabled &&
                    mds.Type == "cyan" &&
                    mds.TopicName == topic) {
                    this.runMacro(m, srcIndex, node);
                }
            });
        });
    }
    // --------------------------------------------------------------------------------------------
    private dataRegisterUpdated = (regNames: string[]) => {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        if (!isDefined(node))
            return;
        for (let regName of regNames) {
            // Detect the macros that use it and dispatch async processing
            let macros = this.MacroToRegisterMapping[regName];
            if (isDefined(macros)) {
                _(macros)
                    .orderBy(x => x.Settings.Order, "asc")
                    .forEach(m => {
                        let srcIndex = _.findIndex(m.Settings.MacroDataSrc, mds =>
                            mds.Enabled &&
                            mds.Type == "register" &&
                            mds.RegisterName == regName
                        );
                        if (m.Enabled &&
                            srcIndex > -1)
                            this.runMacro(m, srcIndex, node);
                    });
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private dataWidgetPortUpdated = (info: IWidgetDataUpdate) => {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        if (!isDefined(node))
            return;
        node.WidgetUpdateData(info.ThingKey, info.PortKey, info.Data);
    }
    // --------------------------------------------------------------------------------------------
    // Key is the thing key
    private MacroDataLoadingCounting: { [key: string]: number };
    private macroDataLoadingState(
        node: WebNode,
        macroDataInfo: MacroDataInfo[],
        loadingStatus: boolean) {
        let loadingStates = [];
        _.forEach(macroDataInfo, m => {
            if (m.Type == "port" &&
                m.UILoading &&
                m.Enabled) {
                // Seperate the count-up from count-down
                if (loadingStatus) {
                    // count-up 
                    if (yHas(this.MacroDataLoadingCounting, m.ThingKey))
                        this.MacroDataLoadingCounting[m.ThingKey]++
                    else
                        this.MacroDataLoadingCounting[m.ThingKey] = 1;

                    // For enable loading send it every time !!!
                    loadingStates.push({
                        thingKey: m.ThingKey,
                        loading: true
                    });
                } else {
                    // Count-down
                    if (_.has(this.MacroDataLoadingCounting, m.ThingKey))
                        this.MacroDataLoadingCounting[m.ThingKey]--;

                    if (this.MacroDataLoadingCounting[m.ThingKey] == 0)
                        loadingStates.push({
                            thingKey: m.ThingKey,
                            loading: false
                        });
                }
            }
        });
        node.ThingLoadingStatusUpdate(loadingStates);
    }
    // --------------------------------------------------------------------------------------------
    public setCyanRegister(topic: string, data: any) {
        if (isStringEmpty(topic))
            return;
        this.onDataCyanUpdate.dispatchAsync([topic, data]);
    }
    // --------------------------------------------------------------------------------------------
    private async runMacroInternal(m: MacroInfoEx,
        node: WebNode,
        prepInput: MacroJsPrepInput,
        finishedCB: (success: boolean) => void = _.noop
    ) {
        let loadedAnimationIsStarted = false;
        try {
            let res = await this.MacroLogicEngine.RunMacroSteps(
                {
                    Type: m.MacroType,
                    JSPrepInput: prepInput,
                    JSPrepScript: m.JsPrepScript,
                    JSRespHandlingScript: m.JsHandlingScript,
                    MacroId: m.Guid,
                    MacroVersion: m.Version,
                    ProjectId: this.BoardId,
                    QueryConnectionString: m.ConnectionString,
                    QueryScript: m.QueryScript,
                    RestSettings: m.RestSettings
                },
                (state) => {
                    if (state == "Query") {
                        this.macroDataLoadingState(node, m.Settings.MacroDataSrc, true);
                        this.macroDataLoadingState(node, m.Settings.MacroDataDst, true);
                        loadedAnimationIsStarted = true;
                    }
                });

            let resultDictionary = null;
            if (m.MacroType == ModelViews.Macros.MacroScriptType.Javascript) {
                // In javascript execution we have only one step
                resultDictionary = res.JSPrepResults;
            } else {
                resultDictionary = res.JSRespHandlingResults;
            }
            let jsHandlingDst = {};
            let dispatchAsyncRegisterUpdates: string[] = [];
            // Pipe the data to correct thing
            if (isDefined(resultDictionary)) {
                for (let md of m.Settings.MacroDataDst) {
                    let handlingKey = (md.Type == "port") ? (md.ThingKey + "->" + md.PortKey) : md.RegisterName;
                    if (md.Enabled) {
                        let data = _.get(resultDictionary, md.VariableName);
                        if (!_.isUndefined(data)) {
                            jsHandlingDst[handlingKey] = {
                                VariableName: md.VariableName,
                                Status: "OK"
                            };
                            if (md.Type == "port") {
                                this.dataWidgetPortUpdated({
                                    ThingKey: md.ThingKey,
                                    PortKey: md.PortKey,
                                    Data: data
                                });
                            } else if (md.Type == "register") {
                                this.Register[md.RegisterName] = data;
                                dispatchAsyncRegisterUpdates.push(md.RegisterName);

                                // In the case of persistance usage save it
                                if (md.RegisterIsPersistence)
                                    localStorage.setItem(this.getLocalStorageKey(md.RegisterName), JSON.stringify(data));
                            } else if (md.Type == "cyan") {
                                let boardThing = BoardThings.GetInstance(this.NodeKey);
                                if (isDefined(boardThing))
                                    boardThing.sendGemData(md.TopicName, data);
                            }

                        } else {
                            jsHandlingDst[handlingKey] = {
                                VariableName: md.VariableName,
                                Status: "No Data"
                            };
                        }
                    } else {
                        jsHandlingDst[handlingKey] = {
                            VariableName: md.VariableName,
                            Status: "Disabled"
                        };
                    }
                }
            }

            // Dispatch registers update after passing all registers
            if (dispatchAsyncRegisterUpdates.length > 0)
                this.onDataRegisterUpdate.dispatchAsync(dispatchAsyncRegisterUpdates);

            // Save to debug list for debugging
            this.logEvent(m, res, prepInput, jsHandlingDst);

            // return success
            if (loadedAnimationIsStarted) {
                this.macroDataLoadingState(node, m.Settings.MacroDataSrc, false);
                this.macroDataLoadingState(node, m.Settings.MacroDataDst, false);
            }
            finishedCB(true);
        } catch (errors) {
            // Save to debug list for debugging
            this.logEvent(m, errors, prepInput, {});

            // return failed
            if (loadedAnimationIsStarted) {
                this.macroDataLoadingState(node, m.Settings.MacroDataSrc, false);
                this.macroDataLoadingState(node, m.Settings.MacroDataDst, false);
            }
            finishedCB(false);
        }
    }
    // --------------------------------------------------------------------------------------------
    private runMacro(m: MacroInfoEx,
        srcIndex: number,
        node: WebNode,
        finishedCB: (success: boolean) => void = _.noop
    ) {
        let prepInput = AggregateMacroDataSrc(this.NodeKey, m.Settings.MacroDataSrc, srcIndex, this.Register, this.CyanRegister);
        if (m.Settings.UserValidationForm?.Enabled == true) {
            // Open dialog and wait user response
            MacroUserValidationFormManager.AddUserValidationDialog({
                info: m.Settings.UserValidationForm,
                resultCB: (accepted, formData) => {
                    if (accepted) {
                        // The user say ok to run the macro 
                        this.runMacroInternal(m, node, prepInput, finishedCB);
                    } else {
                        this.logEvent(m, { StoppedFromUser: true }, prepInput, {});
                        finishedCB(false);
                    }
                }
            })
        } else {
            // Run the macro internal
            this.runMacroInternal(m, node, prepInput, finishedCB);
        }
    }
    // --------------------------------------------------------------------------------------------
    private logEvent(m: MacroInfoEx,
        res: MacroLogicResults,
        dataSrc: { [key: string]: any; },
        jsHandlingDst: { [key: string]: any; }) {
        const gemDebug = getGlobal("GemDebug");
        // store status only in edit mode 
        if (!this.inEditMode && gemDebug != true)
            return;

        if (!yHas(this.MacroDebugHistory, m.Guid))
            this.MacroDebugHistory[m.Guid] = {
                Macro: m,
                LastStateHaveErrors: isDefined(res.Error),
                Log: []
            };

        // Limit the log to only 10 
        if (this.MacroDebugHistory[m.Guid].Log.length > 10)
            this.MacroDebugHistory[m.Guid].Log.pop();

        // Push the new log status
        this.MacroDebugHistory[m.Guid].Log.unshift({
            Timestamp: new Date().toISOString(),
            Error: res.Error,
            JSPrepInput: dataSrc,
            JsPrepResults: res.JSPrepResults,
            JsHandlingResults: res.JSRespHandlingResults,
            QueryResults: res.QueryResults,
            QueryMetadata: res.QueryMetadata,
            JsHandlingDst: jsHandlingDst,
            Performance: res.Performance,
            StoppedFromUser: res.StoppedFromUser
        });
        this.MacroDebugHistory[m.Guid].LastStateHaveErrors = isDefined(res.Error) && res.Error.length > 0;
    }
    // --------------------------------------------------------------------------------------------
    // Token SM
    // --------------------------------------------------------------------------------------------
    private static async StartTokenSM() {
        // Don't use token SM for user managment
        if (process.env.GLOW_BUILD)
            return;
        // Request new token
        DebugEx.TraceLog("Request new token");
        try {
            const response = await fetch(ServerPaths["GetAccessToken"].Path, {
                method: ServerPaths["GetAccessToken"].Method,
                mode: 'cors',
                cache: "no-cache",
                credentials: "include"
            });

            var token = await response.text();

            DebugEx.TraceLog("New Token");
            var data: any = jwt_decode(token);
            BoardMacroEngine._tokenInstance = {
                Timeout: new Date(data.exp * 1000),
                Token: token,
                Data: data
            };

            // Set for the next update windows
            let diff = BoardMacroEngine._tokenInstance.Timeout.getTime() - new Date().getTime();
            if (diff < 0)
                diff = 1000;
            window.setTimeout(BoardMacroEngine.StartTokenSM, diff)
        } catch (ex) {
            DebugEx.TraceException(ex, "Failed to get access token");

            // Failed get token test again after 30 sec 
            window.setTimeout(BoardMacroEngine.StartTokenSM, 30000);
        }

    }
    // --------------------------------------------------------------------------------------------
    public static GetAccessToken() {
        return this._tokenInstance?.Token;
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------